// /src/QuotationList.js
import React, { useEffect, useState } from "react";
import api from "./services/api";
import generatePDF from "./PDFGenerator";
import debounce from "lodash.debounce";
import { useAuth } from "./context/AuthContext";
import { Link } from "react-router-dom";
import ReminderModal from "./ReminderModal";
import FollowUpWidget from "./FollowUpWidget";

const QuotationList = () => {
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [loadingAll, setLoadingAll] = useState(true);
  const [loadingView, setLoadingView] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [waFeedback, setWaFeedback] = useState("");

  const limit = 10;

  useEffect(() => {
    fetchPaginatedData(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const fetchPaginatedData = (currentPage, searchTerm = "") => {
    setLoadingAll(true);
    api
      .get(`/quotations/paginated`, {
        params: { page: currentPage, limit, search: searchTerm },
      })
      .then((res) => {
        setQuotations(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      })
      .catch((err) => {
        console.error(err);
        setQuotations([]);
      })
      .finally(() => setLoadingAll(false));
  };

  const handleView = (quotationId) => {
    setLoadingView(true);
    api
      .get(`/quotations/${quotationId}`)
      .then((res) => setSelectedQuotation(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoadingView(false));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quotation? This action cannot be undone.")) {
      return;
    }

    try {
      await api.delete(`/quotations/delete/${id}`);
      fetchPaginatedData(page, search);
      if (selectedQuotation && selectedQuotation.id === id) {
        setSelectedQuotation(null);
      }
    } catch (error) {
      console.error("Failed to delete quotation:", error);
      alert("Failed to delete quotation. Please try again.");
    }
  };

  const handleSendDirectWhatsApp = async (q) => {
    if (!q || !q.mobile) {
      alert("Customer mobile number is missing.");
      return;
    }

    try {
      setSendingWhatsApp(true);
      setWaFeedback("");

      const pdfBase64 = generatePDF({
        customer: {
          name: q.customer_name,
          city: q.city,
          date: q.date ? new Date(q.date).toLocaleDateString("en-GB") : "",
          mobile: q.mobile,
          quotationNo: q.quotation_no,
        },
        categoryNames: [],
        selectedItems: q.items,
        total: Number(q.total),
        discountAmount: Number(q.discount_amount),
        discountPercent: q.discount_percent,
        grandTotal: Number(q.net_total),
        includeGst: q.include_gst !== 0 && q.include_gst !== false,
      }, "base64");

      const res = await api.post("/whatsapp/send-quotation", {
        mobile: q.mobile,
        pdfBase64,
        quotationNo: q.quotation_no,
        customerName: q.customer_name,
        grandTotal: q.net_total,
      });

      setWaFeedback(res.data?.message || "PDF delivered on WhatsApp!");
      setTimeout(() => setWaFeedback(""), 4000);
    } catch (err) {
      console.error(err);
      const errMsg =
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Failed to send PDF on WhatsApp. Ensure WhatsApp is connected in Navbar.";
      setWaFeedback("❌ " + errMsg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSearchChange = debounce((value) => {
    setPage(1);
    setSearch(value);
  }, 400);

  const rupee = (val) =>
    typeof val === "number"
      ? val.toLocaleString("en-IN", { maximumFractionDigits: 0 })
      : val;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white overflow-x-hidden pb-20">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-purple-600/20 rounded-full blur-[100px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-indigo-600/20 rounded-full blur-[100px]" style={{ animationDelay: "-5s" }}></div>
        <div className="orb absolute w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-600/10 rounded-full blur-[100px]" style={{ animationDelay: "-7s" }}></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 relative z-10 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 inline-block">
            All Quotations
          </h1>
          <p className="text-purple-300/60 mt-2 text-sm uppercase tracking-widest font-medium">Manage and track your client quotations</p>
        </div>

        {/* Search + Actions Bar */}
        <div className="glass-panel p-6 mb-8 rounded-3xl border border-white/10 shadow-2xl backdrop-blur-xl bg-white/5 relative overflow-hidden">
           <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none"></div>
           
           <div className="relative z-10 flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="relative flex-1 w-full max-w-2xl">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-purple-300/50 text-lg"></i>
              <input
                type="text"
                placeholder="Search by customer, city, mobile or quotation number..."
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-900/50 border border-white/10 focus:border-purple-500/50 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-white placeholder-white/20 transition-all font-medium"
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            
            <Link to="/maker" className="group relative px-8 py-4 rounded-xl font-bold text-white shadow-lg overflow-hidden transition-all hover:scale-[1.02] w-full md:w-auto text-center">
                 <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 transition-all group-hover:opacity-90"></div>
                 <div className="relative flex items-center justify-center gap-2">
                    <i className="fas fa-plus"></i> New Quotation
                 </div>
            </Link>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block glass-panel rounded-3xl overflow-hidden border border-white/10 shadow-xl bg-slate-900/30 backdrop-blur-md">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 text-purple-200 text-xs uppercase tracking-wider font-bold border-b border-white/10">
                  <th className="px-6 py-5 text-left">Quotation No</th>
                  <th className="px-6 py-5 text-left">Customer</th>
                  <th className="px-6 py-5 text-left">City</th>
                  <th className="px-6 py-5 text-left">Date</th>
                  <th className="px-6 py-5 text-left">Amount</th>
                  <th className="px-6 py-5 text-center">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/5">
                {loadingAll ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                         <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin"></div>
                         <span className="text-purple-300/50 text-sm">Loading quotations...</span>
                      </div>
                    </td>
                  </tr>
                ) : quotations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center text-white/30 italic">
                      No quotations found matching your search.
                    </td>
                  </tr>
                ) : (
                  quotations.map((q, idx) => (
                    <tr key={q.id || idx} className="group hover:bg-white/5 transition-colors">
                      <td className="px-6 py-5 font-mono text-purple-300 font-medium">
                         <span className="bg-purple-500/10 text-purple-300 px-2 py-1 rounded-md text-sm border border-purple-500/20">
                            {q.quotation_no}
                         </span>
                      </td>
                      <td className="px-6 py-5 font-medium text-white">
                          <div className="flex flex-col">
                              <span>{q.customer_name}</span>
                              <span className="text-xs text-white/30 font-normal">{q.mobile}</span>
                          </div>
                      </td>
                      <td className="px-6 py-5 text-purple-200/70">{q.city}</td>
                      <td className="px-6 py-5 text-sm text-white/50 font-mono">
                          {q.date ? new Date(q.date).toLocaleDateString('en-GB') : "-"}
                      </td>
                      <td className="px-6 py-5 font-bold text-lg text-emerald-400 font-mono">
                          ₹{rupee(Number(q.net_total) || 0)}
                      </td>
                      <td className="px-6 py-5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleView(q.id)}
                            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 text-purple-300 hover:text-white transition-all flex items-center justify-center border border-white/5"
                            title="View Details"
                          >
                            <i className="fas fa-eye text-sm"></i>
                          </button>
                          <button
                            onClick={() => setReminderTarget(q)}
                            className={`h-9 px-3 rounded-full transition-all flex items-center gap-1.5 border text-xs font-semibold ${
                              Number(q.reminder_count) > 0
                                ? "bg-amber-500/15 border-amber-500/30 text-amber-300 hover:bg-amber-500/25 shadow-sm shadow-amber-950/40"
                                : "bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] border-[#25D366]/20"
                            }`}
                            title={Number(q.reminder_count) > 0 ? `WhatsApp Reminder sent ${q.reminder_count} time(s)` : "Send WhatsApp Follow-up Reminder"}
                          >
                            <i className="fas fa-bell text-xs"></i>
                            {Number(q.reminder_count) > 0 ? (
                              <span className="font-bold whitespace-nowrap">
                                {q.reminder_count} time{Number(q.reminder_count) > 1 ? "s" : ""}
                              </span>
                            ) : (
                              <span className="opacity-80">Remind</span>
                            )}
                          </button>
                          <button
                            onClick={() => window.location.href = `/maker?clone=${q.id}`}
                            className="w-10 h-10 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 transition-all flex items-center justify-center border border-emerald-500/20"
                            title="Duplicate / Clone as New"
                          >
                            <i className="fas fa-clone text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {loadingAll ? (
             <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin mx-auto mb-3"></div>
                <span className="text-purple-300/50 text-sm">Loading quotations...</span>
             </div>
          ) : quotations.length === 0 ? (
            <div className="text-center text-white/30 italic p-8 glass-panel rounded-2xl bg-white/5">
              No quotations found.
            </div>
          ) : (
            quotations.map((q, idx) => (
              <div key={q.id || idx} className="glass-panel rounded-2xl p-5 border border-white/10 bg-slate-900/40 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-indigo-500 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="flex justify-between items-start mb-3 pl-3">
                  <div>
                    <h3 className="font-bold text-white text-lg">{q.customer_name}</h3>
                    <div className="text-white/40 text-sm flex items-center gap-2">
                       <i className="fas fa-map-marker-alt text-xs"></i> {q.city}
                    </div>
                  </div>
                  <span className="bg-purple-500/10 text-purple-300 px-2 py-1 rounded text-xs border border-purple-500/20 font-mono">
                    #{q.quotation_no}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 pl-3">
                  <div>
                    <p className="text-[10px] text-purple-300/50 uppercase tracking-wider font-bold">Date</p>
                    <p className="text-sm text-white/80 font-mono">{q.date ? new Date(q.date).toLocaleDateString('en-GB') : "-"}</p>
                  </div>
                  <div className="text-right">
                     <p className="text-[10px] text-purple-300/50 uppercase tracking-wider font-bold">Amount</p>
                     <p className="text-lg font-bold text-emerald-400 font-mono">₹{rupee(Number(q.net_total) || 0)}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleView(q.id)}
                    className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                  >
                    View Details <i className="fas fa-arrow-right text-xs opacity-50"></i>
                  </button>
                  <button
                    onClick={() => setReminderTarget(q)}
                    className={`border py-3 px-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1.5 ${
                      Number(q.reminder_count) > 0
                        ? "bg-amber-500/15 border-amber-500/30 text-amber-300 hover:bg-amber-500/25"
                        : "bg-[#25D366]/10 hover:bg-[#25D366]/20 border-[#25D366]/20 text-[#25D366]"
                    }`}
                    title="Send WhatsApp Follow-up Reminder"
                  >
                    <i className="fas fa-bell text-xs"></i>
                    <span>
                      {Number(q.reminder_count) > 0
                        ? `${q.reminder_count} time${Number(q.reminder_count) > 1 ? "s" : ""}`
                        : "Remind"}
                    </span>
                  </button>
                  <button
                    onClick={() => window.location.href = `/maker?clone=${q.id}`}
                    className="bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 py-3 px-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1.5"
                    title="Duplicate Quotation"
                  >
                    <i className="fas fa-clone text-xs"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
             <button
               onClick={() => setPage(Math.max(1, page - 1))}
               disabled={page === 1}
               className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                   page === 1 
                   ? "text-white/20 cursor-not-allowed" 
                   : "text-purple-300 hover:bg-white/5 hover:text-white"
               }`}
             >
               <i className="fas fa-chevron-left"></i> Previous
             </button>

             <div className="flex gap-2">
               {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                   let p = i + 1;
                   // Simple logic to show current page range (can be improved)
                   if (totalPages > 5 && page > 3) p = page - 2 + i;
                   if (p > totalPages) return null;

                   return (
                       <button
                           key={p}
                           onClick={() => setPage(p)}
                           className={`w-10 h-10 rounded-xl font-bold text-sm flex items-center justify-center transition-all ${
                               page === p
                               ? "bg-purple-600 text-white shadow-lg shadow-purple-900/50"
                               : "bg-white/5 text-purple-200 hover:bg-white/10"
                           }`}
                       >
                           {p}
                       </button>
                   );
               })}
             </div>

             <button
               onClick={() => setPage(Math.min(totalPages, page + 1))}
               disabled={page === totalPages}
               className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                   page === totalPages 
                   ? "text-white/20 cursor-not-allowed" 
                   : "text-purple-300 hover:bg-white/5 hover:text-white"
               }`}
             >
               Next <i className="fas fa-chevron-right"></i>
             </button>
        </div>

        <div className="text-center mt-12">
            <p className="text-white/10 text-xs font-medium uppercase tracking-widest">Avantica Pumps Pvt Ltd</p>
        </div>
      </div>

      {/* View Quotation Modal */}
      {(loadingView || selectedQuotation) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-slate-900 border border-white/10 shadow-2xl flex flex-col">
            
            {/* Modal Close */}
            <button
               onClick={() => setSelectedQuotation(null)}
               className="absolute top-4 right-4 z-40 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
            >
               <i className="fas fa-times"></i>
            </button>

            {loadingView ? (
                 <div className="flex flex-col items-center justify-center h-64">
                    <div className="w-10 h-10 border-4 border-purple-500/30 border-t-purple-400 rounded-full animate-spin mb-4"></div>
                    <p className="text-purple-300/50 text-sm">Loading details...</p>
                 </div>
            ) : (
                <>
                   {/* Modal Header */}
                   <div className="p-8 border-b border-white/5 bg-white/[0.02]">
                       <div className="flex items-center gap-3 mb-2">
                           <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold border border-purple-500/20 uppercase tracking-wider">
                               Quotation
                           </span>
                           <span className="text-white/30 text-sm font-mono">{selectedQuotation.date ? new Date(selectedQuotation.date).toLocaleDateString('en-GB') : ""}</span>
                       </div>
                       <h2 className="text-3xl font-bold text-white mb-1">
                          {selectedQuotation.customer_name}
                       </h2>
                       <p className="text-white/50 text-lg">{selectedQuotation.quotation_no}</p>
                   </div>

                   {/* Modal Content - Scrollable */}
                   <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                       
                       {/* Info Grid */}
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                           <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
                               <p className="text-xs text-purple-300/50 uppercase tracking-wider font-bold mb-1">Contact</p>
                               <p className="text-white font-mono text-lg">{selectedQuotation.mobile}</p>
                           </div>
                           <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
                               <p className="text-xs text-purple-300/50 uppercase tracking-wider font-bold mb-1">Location</p>
                               <p className="text-white text-lg">{selectedQuotation.city}</p>
                           </div>
                           <div className="bg-gradient-to-br from-emerald-900/20 to-teal-900/20 rounded-2xl p-5 border border-emerald-500/20">
                               <p className="text-xs text-emerald-400/50 uppercase tracking-wider font-bold mb-1">Net Amount</p>
                               <p className="text-emerald-400 font-mono text-xl font-bold">₹{rupee(Number(selectedQuotation.net_total) || 0)}</p>
                           </div>
                       </div>

                       <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                           <i className="fas fa-box-open text-purple-400"></i> Items
                       </h3>
                       
                       <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/5 mb-8">
                           <table className="w-full text-sm text-left">
                               <thead className="text-xs text-purple-200 uppercase bg-white/5 font-bold">
                                   <tr>
                                       <th className="px-6 py-4">Item Name</th>
                                       <th className="px-6 py-4 text-right">Price</th>
                                       <th className="px-6 py-4 text-center">Qty</th>
                                       <th className="px-6 py-4 text-right">Total</th>
                                   </tr>
                               </thead>
                               <tbody className="divide-y divide-white/5">
                                  {Array.isArray(selectedQuotation.items) && selectedQuotation.items.map((item, idx) => (
                                      <tr key={idx} className="hover:bg-white/[0.02]">
                                          <td className="px-6 py-4 font-medium text-white">
                                              {item.name}
                                              {item.description && <div className="text-xs text-white/50 mt-1">{item.description}</div>}
                                          </td>
                                          <td className="px-6 py-4 text-right text-white/70">
                                              ₹{rupee(
                                                  (selectedQuotation.include_gst !== 0 && selectedQuotation.include_gst !== false)
                                                      ? (Number(item.price) || 0) / 1.18
                                                      : (Number(item.price) || 0)
                                              )}
                                          </td>
                                          <td className="px-6 py-4 text-center text-white/70">{item.quantity}</td>
                                          <td className="px-6 py-4 text-right text-purple-200 font-bold">
                                              ₹{rupee(
                                                  ((selectedQuotation.include_gst !== 0 && selectedQuotation.include_gst !== false)
                                                      ? (Number(item.price) || 0) / 1.18
                                                      : (Number(item.price) || 0)) * (Number(item.quantity) || 0)
                                              )}
                                          </td>
                                      </tr>
                                  ))}
                               </tbody>
                           </table>
                       </div>

                       {/* Summary & Totals */}
                       <div className="flex flex-col items-end gap-2 border-t border-white/10 pt-6 mb-8">
                           { (Number(selectedQuotation.discount_amount) || Number(selectedQuotation.discount_percent)) > 0 && (
                               <div className="flex items-center gap-8 text-sm mb-2">
                                   <span className="text-white/50">Discount</span>
                                   <span className="text-red-400 font-bold font-mono">
                                       - ₹{rupee(Number(selectedQuotation.discount_amount) || (Number(selectedQuotation.total) * (Number(selectedQuotation.discount_percent) || 0) / 100))}
                                   </span>
                               </div>
                           )}
                           { (selectedQuotation.include_gst !== 0 && selectedQuotation.include_gst !== false) ? (
                               <React.Fragment>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">Taxable Amount</span>
                                       <span className="text-white font-bold font-mono">₹{rupee((Number(selectedQuotation.net_total) || 0) / 1.18)}</span>
                                   </div>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">CGST (9%)</span>
                                       <span className="text-white font-bold font-mono">₹{rupee(((Number(selectedQuotation.net_total) || 0) - ((Number(selectedQuotation.net_total) || 0) / 1.18)) / 2)}</span>
                                   </div>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">SGST (9%)</span>
                                       <span className="text-white font-bold font-mono">₹{rupee(((Number(selectedQuotation.net_total) || 0) - ((Number(selectedQuotation.net_total) || 0) / 1.18)) / 2)}</span>
                                   </div>
                               </React.Fragment>
                           ) : (
                               <div className="flex items-center gap-8 text-sm">
                                   <span className="text-white/50">Total Amount</span>
                                   <span className="text-white font-bold font-mono">₹{rupee(Number(selectedQuotation.total) || 0)}</span>
                               </div>
                           )}
                           <div className="w-full h-px bg-white/10 my-2 max-w-xs"></div>
                           <div className="flex items-center gap-8 text-xl">
                               <span className="text-emerald-400 font-bold">Net Payable</span>
                               <span className="text-emerald-400 font-black font-mono">₹{rupee(Number(selectedQuotation.net_total) || 0)}</span>
                           </div>
                       </div>
                   </div>

                    {/* WhatsApp Feedback Banner */}
                    {waFeedback && (
                      <div className={`mx-6 mb-2 p-3 rounded-xl text-xs font-bold text-center ${waFeedback.startsWith("❌") ? "bg-red-500/20 text-red-300 border border-red-500/30" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 animate-pulse"}`}>
                        {waFeedback}
                      </div>
                    )}

                   {/* Modal Actions Footer */}
                   <div className="p-6 border-t border-white/10 bg-white/5 flex flex-wrap items-center justify-end gap-3">
                        {/* Edit */}
                         <button
                           onClick={() => {
                             if (!selectedQuotation || !selectedQuotation.id) return;
                             window.location.href = `/edit-quotation/${selectedQuotation.id}`;
                           }}
                           className="px-5 py-3 rounded-xl font-bold bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all flex items-center gap-2"
                         >
                           <i className="fas fa-edit text-amber-400"></i> Edit
                         </button>

                        {/* Duplicate / Clone */}
                        <button
                          onClick={() => {
                            if (!selectedQuotation || !selectedQuotation.id) return;
                            window.location.href = `/maker?clone=${selectedQuotation.id}`;
                          }}
                          className="px-5 py-3 rounded-xl font-bold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all flex items-center gap-2"
                        >
                          <i className="fas fa-clone"></i> Duplicate
                        </button>
                        
                        {/* Direct Send PDF to WhatsApp */}
                        <button
                          onClick={() => handleSendDirectWhatsApp(selectedQuotation)}
                          disabled={sendingWhatsApp}
                          className="px-5 py-3 rounded-xl font-bold bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-green-900/30 transition-all flex items-center gap-2"
                        >
                          {sendingWhatsApp ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              <span>Sending PDF...</span>
                            </>
                          ) : (
                            <>
                              <i className="fab fa-whatsapp text-lg"></i>
                              <span>Send PDF on WhatsApp</span>
                            </>
                          )}
                        </button>

                        {/* Send WhatsApp Reminder */}
                        <button
                          onClick={() => setReminderTarget(selectedQuotation)}
                          className={`px-5 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border ${
                            Number(selectedQuotation.reminder_count) > 0
                              ? "bg-amber-500/20 border-amber-500/40 text-amber-300 hover:bg-amber-500/30 shadow-lg shadow-amber-950/30"
                              : "bg-amber-500/10 border-amber-500/20 text-amber-300 hover:bg-amber-500/20"
                          }`}
                          title="Send WhatsApp Follow-up Reminder"
                        >
                          <i className="fas fa-bell"></i>
                          <span>
                            Send Reminder
                            {Number(selectedQuotation.reminder_count) > 0
                              ? ` (Sent ${selectedQuotation.reminder_count} time${Number(selectedQuotation.reminder_count) > 1 ? "s" : ""})`
                              : ""}
                          </span>
                        </button>

                        {/* WhatsApp Web Fallback */}
                        <button
                          onClick={() => {
                            const phone = (selectedQuotation.mobile || "").replace(/\D/g, "");
                            const message = encodeURIComponent(
                              `Hello ${selectedQuotation.customer_name}, here is your quotation #${selectedQuotation.quotation_no}.`
                            );
                            window.open(`https://wa.me/91${phone}?text=${message}`, "_blank");
                          }}
                          className="px-4 py-3 rounded-xl font-bold bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition-all flex items-center gap-2 text-xs"
                          title="Open WhatsApp Web chat link"
                        >
                          <i className="fab fa-whatsapp"></i> Chat Link
                        </button>

                       {/* Print */}
                       <button
                         onClick={() =>
                           generatePDF({
                             customer: {
                               name: selectedQuotation.customer_name,
                               city: selectedQuotation.city,
                               date: selectedQuotation.date
                                 ? new Date(selectedQuotation.date).toLocaleDateString("en-GB")
                                 : "",
                               mobile: selectedQuotation.mobile,
                               quotationNo: selectedQuotation.quotation_no,
                             },
                             categoryNames: [], 
                             selectedItems: selectedQuotation.items,
                             total: Number(selectedQuotation.total),
                             discountAmount: Number(selectedQuotation.discount_amount),
                             discountPercent: selectedQuotation.discount_percent,
                             grandTotal: Number(selectedQuotation.net_total),
                             includeGst: selectedQuotation.include_gst !== 0 && selectedQuotation.include_gst !== false
                           }, "print")
                         }
                         className="px-5 py-3 rounded-xl font-bold bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/20 transition-all flex items-center gap-2"
                       >
                         <i className="fas fa-print"></i> Print
                       </button>

                       {/* PDF */}
                       <button
                         onClick={() =>
                           generatePDF({
                             customer: {
                               name: selectedQuotation.customer_name,
                               city: selectedQuotation.city,
                               date: selectedQuotation.date
                                 ? new Date(selectedQuotation.date).toLocaleDateString("en-GB")
                                 : "",
                               mobile: selectedQuotation.mobile,
                               quotationNo: selectedQuotation.quotation_no,
                             },
                             categoryNames: selectedQuotation.items?.category,
                             selectedItems: selectedQuotation.items,
                             total: Number(selectedQuotation.total),
                             discountAmount: Number(selectedQuotation.discount_amount),
                             discountPercent: selectedQuotation.discount_percent,
                             grandTotal: Number(selectedQuotation.net_total),
                             includeGst: selectedQuotation.include_gst !== 0 && selectedQuotation.include_gst !== false
                           })
                         }
                        className="px-5 py-3 rounded-xl font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/20 transition-all flex items-center gap-2"
                       >
                         <i className="fas fa-file-pdf"></i> Download PDF
                       </button>

                       {/* Delete (Admin only) */}
                       {user?.role === "admin" && (
                         <button
                           onClick={() => handleDelete(selectedQuotation.id)}
                           className="ml-auto px-5 py-3 rounded-xl font-bold bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2"
                         >
                           <i className="fas fa-trash"></i>
                         </button>
                       )}
                   </div>
                </>
            )}
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {reminderTarget && (
        <ReminderModal
          quotation={reminderTarget}
          onClose={() => setReminderTarget(null)}
          onSuccess={(updated) => {
            setQuotations((prev) =>
              prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q))
            );
            if (selectedQuotation && selectedQuotation.id === updated.id) {
              setSelectedQuotation((prev) => ({ ...prev, ...updated }));
            }
          }}
        />
      )}

      {/* Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .orb {
          animation: float 10s ease-in-out infinite;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .glass-panel {
            background: rgba(255, 255, 255, 0.03);
            backdrop-filter: blur(16px);
        }
      `}</style>
    </div>
  );
};

export default QuotationList;
